import React, { useCallback, useEffect, useState } from 'react';
import { Box, Modal, Typography, IconButton, CircularProgress } from '@mui/material';
import { X, ChevronRight, CornerDownRight } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { formatMoneda } from '../state/format';
import { EstadoBolsa, ESTADOS_TERMINALES } from '../state/bolsaMachine';
import { centroApi } from '../api/centroApi';
import { BolsaConReprecio, CentroApiError, DetalleCentro, ReprecioResumen } from '../types';
import { StatusBadge } from './StatusBadge';
import { SendQuoteModal } from './SendQuoteModal';
import { ReasonDialog } from './ReasonDialog';
import { useToast } from './Toast';
import { ClientBlock } from './ClientBlock';
import { QuoteLines } from './QuoteLines';
import { AvisoEdicionVersion, AvisoEnvioFallido } from './AvisosVersion';
import { VersionesSection } from './VersionesSection';
import { ReprecioResumenModal } from './ReprecioResumenModal';
import { DuplicarModal } from './DuplicarModal';

interface QuoteDrawerProps {
  bolsaId: string;
  onClose: () => void;
  onCambio: () => void;
  onVerEdicion: (bolsaId: string) => void;
  // SPEC-206 (CA-033) — abre el drawer de otra cotización (la original de una copia).
  onAbrirDrawer?: (bolsaId: string) => void;
  // SPEC-206 (UC-009) — la copia se creó: el Centro navega a su Detalle con el resumen.
  onDuplicada?: (resultado: BolsaConReprecio, codigoOrigen: string) => void;
}

// SPEC-206 (RN-012) — estados desde los que se ofrece "Duplicar cotización" (con COT).
// SPEC-206 — acciones que reabren con re-precio: muestran spinner mientras corre la transición.
const ACCIONES_REAPERTURA = ['Ajustar · nueva versión', 'Reactivar y ajustar'];

const ESTADOS_DUPLICABLES = [
  EstadoBolsa.BORRADOR, EstadoBolsa.ENVIADA, EstadoBolsa.VENCIDA, EstadoBolsa.GANADA, EstadoBolsa.PERDIDA, EstadoBolsa.ANULADA,
];

const NOTA_TERMINAL: Record<string, string> = {
  [EstadoBolsa.GANADA]: 'Estado final — lista para convertir en orden de producción.',
  [EstadoBolsa.PERDIDA]: 'Estado final — cerrada con motivo de pérdida registrado.',
  [EstadoBolsa.ANULADA]: 'Estado final — anulada internamente, excluida de las métricas de conversión.',
};

export const QuoteDrawer: React.FC<QuoteDrawerProps> = ({ bolsaId, onClose, onCambio, onVerEdicion, onAbrirDrawer, onDuplicada }) => {
  const { showToast } = useToast();
  const [detalle, setDetalle] = useState<DetalleCentro | null>(null);
  const [loading, setLoading] = useState(true);
  const [mostrarEnvio, setMostrarEnvio] = useState(false);
  const [mostrarDuplicar, setMostrarDuplicar] = useState(false);
  const [transicionEnCurso, setTransicionEnCurso] = useState<EstadoBolsa | null>(null);
  const [resumenReprecio, setResumenReprecio] = useState<{ reprecio: ReprecioResumen; subtitulo: string } | null>(null);
  const [dialogoMotivo, setDialogoMotivo] = useState<{ to: EstadoBolsa; titulo: string; label: string } | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await centroApi.obtenerDetalle(bolsaId);
      setDetalle(data);
    } finally {
      setLoading(false);
    }
  }, [bolsaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const closeIconSize = useIconScale(18);
  const chevronRightSize = useIconScale(15);

  if (loading || !detalle) {
    return (
      <Modal open onClose={onClose}>
        <Box sx={{ position: 'fixed', top: 0, right: 0, width: 410, maxWidth: '94vw', height: '100%', backgroundColor: TOKENS.bg }} />
      </Modal>
    );
  }

  const { bolsa, lineas, versiones, transiciones, creado_en } = detalle;
  const estado = bolsa.estado_codigo;
  const esTerminal = ESTADOS_TERMINALES.includes(estado);
  // SPEC-206 (RN-010) — solo llegan versiones enviadas; la vigente es la de mayor número.
  const vigente = versiones.find((v) => v.es_vigente) ?? null;
  const version = vigente?.version ?? 0;
  const siguienteVersion = detalle.siguiente_version ?? version + 1;
  const codigo = bolsa.codigo_cotizacion ?? bolsa.id;
  const puedeDuplicar = !!bolsa.codigo_cotizacion && ESTADOS_DUPLICABLES.includes(estado);

  const ejecutarTransicion = async (to: EstadoBolsa, reason?: string) => {
    setTransicionEnCurso(to);
    try {
      const { reprecio } = await centroApi.transicionar(bolsa.id, to, reason);
      showToast(`${codigo} → ${to}`);
      // SPEC-206 (CA-008) — al reabrir se muestra qué cambió con los precios de hoy.
      if (reprecio) {
        setResumenReprecio({ reprecio, subtitulo: `${codigo} quedó en borrador para la v${siguienteVersion}` });
      }
      onCambio();
    } catch (error) {
      if (error instanceof CentroApiError) {
        showToast(error.message, 'error');
      } else {
        showToast('No se pudo ejecutar la transición', 'error');
      }
    } finally {
      setTransicionEnCurso(null);
      cargar();
    }
  };

  const duplicada = (resultado: BolsaConReprecio) => {
    setMostrarDuplicar(false);
    onCambio();
    onDuplicada?.(resultado, codigo);
  };

  const acciones: { label: string; primary?: boolean; onClick: () => void; nota?: string }[] = [];
  if (estado === EstadoBolsa.BORRADOR) {
    acciones.push({ label: 'Enviar al cliente', primary: true, onClick: () => setMostrarEnvio(true) });
    acciones.push({
      label: 'Anular',
      onClick: () => setDialogoMotivo({ to: EstadoBolsa.ANULADA, titulo: 'Anular cotización', label: 'Motivo de anulación' }),
    });
  } else if (estado === EstadoBolsa.ENVIADA) {
    acciones.push({ label: 'Marcar ganada', primary: true, onClick: () => ejecutarTransicion(EstadoBolsa.GANADA) });
    acciones.push({ label: 'Ajustar · nueva versión', onClick: () => ejecutarTransicion(EstadoBolsa.BORRADOR) });
    acciones.push({
      label: 'Marcar perdida',
      onClick: () => setDialogoMotivo({ to: EstadoBolsa.PERDIDA, titulo: 'Marcar como perdida', label: 'Motivo de pérdida' }),
    });
    acciones.push({
      label: 'Anular',
      onClick: () => setDialogoMotivo({ to: EstadoBolsa.ANULADA, titulo: 'Anular cotización', label: 'Motivo de anulación' }),
    });
  } else if (estado === EstadoBolsa.VENCIDA) {
    acciones.push({ label: 'Reactivar y ajustar', primary: true, onClick: () => ejecutarTransicion(EstadoBolsa.BORRADOR) });
  }
  if (puedeDuplicar) {
    acciones.push({ label: 'Duplicar cotización', onClick: () => setMostrarDuplicar(true) });
  }
  const reabriendo = transicionEnCurso === EstadoBolsa.BORRADOR;

  return (
    <>
      <Modal open onClose={onClose}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: 410,
            maxWidth: '94vw',
            height: '100%',
            backgroundColor: TOKENS.bg,
            borderLeft: `1px solid ${TOKENS.hair}`,
            overflowY: 'auto',
            padding: '28px 26px',
          }}
          data-testid="quote-drawer"
        >
          <IconButton onClick={onClose} sx={{ position: 'absolute', top: 20, right: 20 }} data-testid="drawer-close">
            <X size={closeIconSize} />
          </IconButton>

          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, letterSpacing: '2.2px', textTransform: 'uppercase', color: TOKENS.muted }}>
            COTIZACIÓN
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(23), fontWeight: 700, letterSpacing: '-0.5px', color: TOKENS.ink, margin: '4px 0' }}>
            {bolsa.codigo_cotizacion ?? bolsa.id}
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), color: TOKENS.text, marginBottom: '10px' }}>
            {bolsa.cliente_razon_social ?? 'Sin cliente asociado'}
          </Typography>
          <StatusBadge estado={estado} />

          {estado === EstadoBolsa.BORRADOR && vigente && (
            <AvisoEdicionVersion siguienteVersion={siguienteVersion} versionVigente={vigente.version} enviadaEn={vigente.enviada_en} />
          )}
          {detalle.envio_fallido && <AvisoEnvioFallido intentadoEn={detalle.envio_fallido.intentado_en} />}

          <ClientBlock
            cliente={bolsa.cliente_id ? { id: bolsa.cliente_id, razonSocial: bolsa.cliente_razon_social ?? 'Cliente' } : null}
            creadoEn={creado_en}
            onAssign={async (clienteId) => {
              try {
                await centroApi.asociarCliente(bolsa.id, clienteId);
                showToast('Cliente asociado');
                await cargar();
              } catch (error) {
                const message = error instanceof CentroApiError ? error.message : 'No se pudo asociar el cliente';
                showToast(message, 'error');
              }
            }}
          />

          {/* Grilla 2x3 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginTop: '20px',
              paddingTop: '18px',
              borderTop: `1px solid ${TOKENS.hair}`,
            }}
          >
            {[
              ['Total venta', formatMoneda(bolsa.total)],
              ['Versión', version > 0 ? `v${version}` : '—'],
              ['Referencias', String(bolsa.lineas_count)],
              ['Unidades', String(bolsa.unidades_count)],
              ['Bolsa de origen', bolsa.codigo_bolsa ?? '—'],
              ['Validez', bolsa.validez ?? '—'],
              ...(estado === EstadoBolsa.GANADA && bolsa.numero_pedido
                ? ([['Número de pedido', bolsa.numero_pedido]] as [string, string][])
                : []),
            ].map(([label, valor]) => (
              <Box key={label} data-testid={label === 'Número de pedido' ? 'quote-drawer-numero-pedido' : undefined}>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>{label}</Typography>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(16.5), fontWeight: 700, color: TOKENS.ink }}>{valor}</Typography>
              </Box>
            ))}
          </Box>

          {detalle.duplicada_de && (
            <Box
              component="button"
              type="button"
              data-testid="drawer-duplicada-de"
              onClick={() => onAbrirDrawer?.(detalle.duplicada_de!.id)}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: TOKENS.purple }}
            >
              <CornerDownRight size={14} aria-hidden />
              <Typography component="span" sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600 }}>
                Duplicada de {detalle.duplicada_de.codigo_cotizacion ?? detalle.duplicada_de.id}
              </Typography>
            </Box>
          )}

          <QuoteLines lineas={lineas} total={bolsa.total} onVerEdicion={() => onVerEdicion(bolsa.id)} />

          {/* Acciones */}
          <Box sx={{ marginTop: '24px' }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '8px' }}>
              ACCIONES
            </Typography>
            {esTerminal && (
              <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', padding: '12px 14px' }}>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), color: TOKENS.text }}>
                  {NOTA_TERMINAL[estado]}
                </Typography>
              </Box>
            )}
            <>
                {acciones.map((a) => (
                  <Box
                    key={a.label}
                    role="button"
                    data-testid={`drawer-action-${a.label}`}
                    aria-disabled={transicionEnCurso !== null}
                    aria-busy={reabriendo && ACCIONES_REAPERTURA.includes(a.label)}
                    onClick={transicionEnCurso !== null ? undefined : a.onClick}
                    sx={{
                      opacity: transicionEnCurso !== null ? 0.55 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '11px 0',
                      borderBottom: `1px solid ${TOKENS.hair}`,
                      cursor: 'pointer',
                      transition: 'padding-left .15s ease',
                      '&:hover': { paddingLeft: '8px' },
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 600, color: a.primary ? TOKENS.purple : TOKENS.ink }}>
                      {a.label}
                    </Typography>
                    {reabriendo && ACCIONES_REAPERTURA.includes(a.label) ? (
                      <CircularProgress size={14} aria-label="Actualizando precios" />
                    ) : (
                      <ChevronRight size={chevronRightSize} color={TOKENS.faint} />
                    )}
                  </Box>
                ))}
                {estado === EstadoBolsa.ENVIADA && (
                  <Box sx={{ backgroundColor: TOKENS.bg, borderRadius: '12px', padding: '10px 12px', marginTop: '10px' }}>
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>
                      ◷ El paso a Vencida es automático al cumplirse la fecha de validez.
                    </Typography>
                  </Box>
                )}
            </>
          </Box>

          {/* SPEC-206 (UC-007/UC-008) — versiones enviadas, antes del historial */}
          <VersionesSection bolsaId={bolsa.id} codigoCotizacion={codigo} versiones={versiones} />

          {/* Historial */}
          <Box sx={{ marginTop: '26px' }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '10px' }}>
              HISTORIAL
            </Typography>
            <Box sx={{ position: 'relative', paddingLeft: '18px' }}>
              <Box sx={{ position: 'absolute', left: 4, top: 4, bottom: 4, width: 1, backgroundColor: TOKENS.hair }} />
              {[...transiciones]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((t, idx) => (
                  <Box key={t.id} sx={{ position: 'relative', marginBottom: '16px' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -18,
                        top: 4,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        border: `2px solid ${TOKENS.faint}`,
                        backgroundColor: idx === 0 ? TOKENS.purple : '#fff',
                      }}
                    />
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 600, color: TOKENS.ink }}>
                      {t.to_estado}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted }}>
                      {new Date(t.createdAt).toLocaleString('es-CO')}
                    </Typography>
                    {t.nota && (
                      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.text }}>{t.nota}</Typography>
                    )}
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      </Modal>

      {mostrarEnvio && (
        <SendQuoteModal
          bolsa={bolsa}
          siguienteVersion={siguienteVersion}
          versionVigente={vigente}
          onCerrar={() => setMostrarEnvio(false)}
          onEnviada={() => {
            onCambio();
            cargar();
          }}
        />
      )}

      {resumenReprecio && (
        <ReprecioResumenModal
          reprecio={resumenReprecio.reprecio}
          subtitulo={resumenReprecio.subtitulo}
          onCerrar={() => setResumenReprecio(null)}
          onRevisar={() => {
            setResumenReprecio(null);
            onVerEdicion(bolsa.id);
          }}
        />
      )}

      {mostrarDuplicar && (
        <DuplicarModal
          bolsa={bolsa}
          onCerrar={() => setMostrarDuplicar(false)}
          onDuplicada={duplicada}
          onError={(mensaje) => showToast(mensaje, 'error')}
        />
      )}

      {dialogoMotivo && (
        <ReasonDialog
          titulo={dialogoMotivo.titulo}
          label={dialogoMotivo.label}
          onCancelar={() => setDialogoMotivo(null)}
          onConfirmar={(motivo) => {
            ejecutarTransicion(dialogoMotivo.to, motivo);
            setDialogoMotivo(null);
          }}
        />
      )}
    </>
  );
};
